import { useEffect, useState } from 'react';
import type { CompliancePageKind as DbCompliancePageKind } from 'shared';
import { api } from '../../lib/api.js';
import { COMPLIANCE_PAGE_TITLES } from '../../lib/compliance-page-titles.js';

export type CompliancePageKind = 'compliance' | 'privacy-policy' | 'terms-of-use' | 'equal-housing';

const KIND_TO_DB: Record<CompliancePageKind, DbCompliancePageKind> = {
  compliance: 'COMPLIANCE',
  'privacy-policy': 'PRIVACY_POLICY',
  'terms-of-use': 'TERMS_OF_USE',
  'equal-housing': 'EQUAL_HOUSING',
};

/**
 * One component, four routes. Body content is admin-editable (see
 * CompliancePagesPage.tsx, reached from the staff gear menu) — a kind with
 * no saved content yet falls back to the placeholder below, same disclosed-
 * placeholder posture as step 12's notice-pdf.ts. `body` is null while
 * loading and on any fetch failure, both of which render the placeholder
 * too rather than an empty page.
 */
export function CompliancePage({ page }: { page: CompliancePageKind }) {
  const title = COMPLIANCE_PAGE_TITLES[KIND_TO_DB[page]];
  const [body, setBody] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBody(null);
    void api<{ body: string | null }>(`/compliance-pages/${KIND_TO_DB[page]}`)
      .then((result) => {
        if (!cancelled) setBody(result.body);
      })
      .catch(() => {
        // Stays null — renders the placeholder below, same as "loading" and
        // "nothing published yet."
      });
    return () => {
      cancelled = true;
    };
  }, [page]);

  return (
    <section className="public__compliance">
      <h1>{title}</h1>
      {body ? (
        <div className="public__compliance-body" dangerouslySetInnerHTML={{ __html: body }} />
      ) : (
        <p className="public__compliance-placeholder">
          This page is a placeholder. {title} content will be provided by the firm and published
          here before this site goes live.
        </p>
      )}
    </section>
  );
}
